/**
 * Block 12 PR-H5b Commit 1 (ADR-0032) — Presenter Экрана «Сохранённые КП»
 * (ADR-0005 Container/Presenter).
 *
 * Pure presenter (`memo`) — рендерит layout shell (P-046) + 4 P-021 states.
 * Child screen блока 12 → **NO VideoButton** (P-040 — entry screens only;
 * entry для блока 12 это CalculatorEntryView / QuoteEntryPicker).
 *
 * **4 states** (P-021 «idle» сворачивается в «loading»):
 *   - `loading` — fetching listSavedQuotes (role=status text).
 *   - `success` — non-empty array, вертикальный список item-кнопок.
 *   - `empty`   — успешный fetch + 0 КП.
 *   - `error`   — fetch threw (IDB unavailable etc) + retry button.
 *
 * Design system: layout shell (P-046), bg-card + border-accent cards (P-038),
 * text-ink (P-039), palette только через токены (P-045), min-h-tap (P-044),
 * никаких text-cta/text-accent на тексте (P-041). quote.id пользователю НЕ
 * показывается (P-042).
 *
 * Reference:
 *   - `apps/web/src/features/calculator/CalculatorEntryView.tsx` (layout + items)
 *   - `apps/web/src/features/contract/ContractListView.tsx` (4-state эталон)
 */

import { memo } from 'react';

import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';

import type { SavedQuote } from './state/savedQuotesIdb';
import { SavedQuoteCard } from './SavedQuoteCard';

export type SavedQuotesListStatus = 'loading' | 'success' | 'empty' | 'error';

export interface SavedQuotesListViewProps {
  status: SavedQuotesListStatus;
  quotes?: readonly SavedQuote[];
  onSelect: (id: string) => void;
  /** Callback после confirm delete КП. Container вызывает `deleteSavedQuote` + refetch list. */
  onDelete: (id: string) => void;
  onRetry: () => void;
}

export const SavedQuotesListView = memo(function SavedQuotesListView({
  status,
  quotes = [],
  onSelect,
  onDelete,
  onRetry,
}: SavedQuotesListViewProps) {
  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Сохранённые КП" />
      <section className="flex-1 overflow-y-auto pb-20 px-3 pt-3 flex flex-col gap-3">
        {status === 'loading' ? (
          <p
            role="status"
            data-testid="saved-quotes-loading"
            className="text-ink/60 text-center py-8"
          >
            Загружаем сохранённые КП…
          </p>
        ) : null}

        {status === 'error' ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <p
              role="alert"
              data-testid="saved-quotes-error"
              className="text-ink text-base text-center m-0"
            >
              Не удалось загрузить КП. Попробуйте позже.
            </p>
            <button
              type="button"
              onClick={onRetry}
              aria-label="Повторить загрузку КП"
              data-testid="saved-quotes-retry"
              className="min-h-tap rounded-xl border-[1.5px] border-accent bg-card text-ink text-base font-medium px-6 py-2 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90"
            >
              Повторить
            </button>
          </div>
        ) : null}

        {status === 'empty' ? (
          <div
            role="status"
            data-testid="saved-quotes-empty"
            className="bg-card border-[1.5px] border-accent rounded-2xl px-5 py-10 text-center"
          >
            <p className="text-ink text-base m-0">
              У вас пока нет сохранённых КП. Создайте новый — он появится здесь.
            </p>
          </div>
        ) : null}

        {status === 'success' ? (
          <ul role="list" data-testid="saved-quotes-list" className="flex flex-col gap-2.5">
            {quotes.map((quote) => (
              <li key={quote.id}>
                <SavedQuoteCard quote={quote} onSelect={onSelect} onDelete={onDelete} />
              </li>
            ))}
          </ul>
        ) : null}
      </section>
      <BottomBar variant="default" />
    </main>
  );
});
